import React from 'react';
import { GrServices } from "react-icons/gr";
import { FaNetworkWired, FaCheck, FaMapMarkerAlt, FaShieldAlt, FaTruck, FaDatabase } from 'react-icons/fa';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../context/MyContext'
import '../../../css/custom-scrollbar.css'

const LogisticsServicePage = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();

  const services = [
    {
      icon: FaNetworkWired,
      title: 'API Integration',
      description: 'Seamless connectivity with your existing systems through robust and flexible API solutions.',
      accent: 'from-violet-600 via-purple-500 to-fuchsia-500',
      glow: 'group-hover:shadow-purple-500/50',
      ring: 'group-hover:ring-purple-400/50',
      tag: 'Connectivity'
    },
    {
      icon: FaCheck,
      title: 'Order Confirmation',
      description: 'Real-time order tracking and instant confirmation to provide transparency and peace of mind.',
      accent: 'from-emerald-500 via-teal-500 to-cyan-500',
      glow: 'group-hover:shadow-teal-500/50',
      ring: 'group-hover:ring-teal-400/50',
      tag: 'Real-time'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Fraud Detection',
      description: 'Advanced algorithmic protection to identify and prevent potential fraudulent activities.',
      accent: 'from-rose-500 via-pink-500 to-red-500',
      glow: 'group-hover:shadow-pink-500/50',
      ring: 'group-hover:ring-pink-400/50',
      tag: 'Protection'
    },
    {
      icon: FaShieldAlt,
      title: 'Security Protocols',
      description: 'Comprehensive security measures to protect your data and ensure compliance.',
      accent: 'from-blue-600 via-indigo-500 to-violet-500',
      glow: 'group-hover:shadow-indigo-500/50',
      ring: 'group-hover:ring-indigo-400/50',
      tag: 'Compliance'
    },
    {
      icon: FaTruck,
      title: 'Logistics Tracking',
      description: 'End-to-end tracking of shipments with real-time updates and precise location monitoring.',
      accent: 'from-orange-500 via-amber-500 to-yellow-500',
      glow: 'group-hover:shadow-amber-500/50',
      ring: 'group-hover:ring-amber-400/50',
      tag: 'Visibility'
    },
    {
      icon: FaDatabase,
      title: 'Warehousing and fulfillment',
      description: "Efficient storage, inventory management, and order fulfillment solutions to streamline logistics and enhance supply chain efficiency.",
      accent: 'from-sky-500 via-cyan-500 to-blue-500',
      glow: 'group-hover:shadow-sky-500/50',
      ring: 'group-hover:ring-sky-400/50',
      tag: 'Fulfillment'
    }
  ];

  const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen w-full flex flex-col overflow-hidden relative transition-colors duration-300 ${
      isDarkMode 
        ? 'dark bg-[#0B1120]' 
        : 'bg-gradient-to-br from-slate-50 via-white to-indigo-50'
    }`}>

      {/* Animated colorful background blobs */}
      <div className="pointer-events-none absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-gradient-to-tr from-cyan-400/40 via-blue-400/30 to-transparent dark:from-cyan-500/10 dark:via-blue-500/5 blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
      {isDarkMode && (
        <>
          <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-gradient-to-tr from-purple-500/15 via-pink-500/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-gradient-to-tr from-indigo-500/10 via-purple-500/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '7s' }} />
        </>
      )}

      <NavBar />
      <div className="flex flex-1 overflow-hidden relative z-10 min-h-0">
                            <div
            className={`
              ${isToggleSidebar ? "w-64" : "w-[72px]"}
              m-3
              rounded-3xl
              ${glass}
              overflow-hidden
              transition-all
              duration-300
              ease-in-out
            `}
          >
            <Sidebar />
          </div>
        <div className="flex-1 flex flex-col p-3 overflow-hidden min-h-0">

          <div className="relative max-w-7xl w-full mx-auto flex flex-col flex-1 min-h-0">

            {/* Hero */}
            <div className="text-center pt-1 pb-2 shrink-0">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-tr from-fuchsia-500 via-purple-500 to-indigo-500 rounded-full blur-2xl opacity-60 animate-pulse"></div>
                  <div className={`relative p-2.5 rounded-full shadow-2xl ring-2 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-[#111827] ring-indigo-500/30 shadow-[0_0_30px_rgba(99,102,241,0.3)]' 
                      : 'bg-white ring-white/80'
                  }`}>
                    <GrServices className={`w-6 h-6 ${isDarkMode ? 'text-indigo-400' : 'text-purple-600'}`} />
                  </div>
                </div>
              </div>

              <span className="inline-block mt-1 mb-1 text-[10px] font-bold tracking-[0.2em] uppercase bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600 dark:from-fuchsia-400 dark:via-purple-400 dark:to-cyan-400 bg-clip-text text-transparent">
                Pickupxpress
              </span>

              <h1 className="text-lg sm:text-xl md:text-2xl font-black tracking-tight leading-tight bg-gradient-to-r from-fuchsia-600 via-purple-600 to-cyan-600 dark:from-fuchsia-400 dark:via-purple-400 dark:to-cyan-400 bg-clip-text text-transparent drop-shadow-sm">
                Logistics Services, Built for Motion
              </h1>
              <p className={`text-xs sm:text-sm max-w-2xl mx-auto mt-0.5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Comprehensive, connected solutions that keep every shipment visible, secure, and on time.
              </p>

              {/* Signature: animated colorful route line */}
              <div className="relative max-w-md mx-auto h-5">
                <svg viewBox="0 0 400 32" className="w-full h-full">
                  <defs>
                    <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#d946ef" />
                      <stop offset="50%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                  <line x1="10" y1="16" x2="390" y2="16" stroke="url(#routeGradient)" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" opacity={isDarkMode ? "0.9" : "0.7"} />
                  <circle cx="10" cy="16" r="4" fill="#d946ef" />
                  <circle cx="390" cy="16" r="4" fill="#06b6d4" />
                  <circle cy="16" r="3" fill="#8b5cf6">
                    <animate attributeName="cx" values="10;390;10" dur="4s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
            </div>

            {/* Bento grid of services — fills remaining screen space, no overflow */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 flex-1 min-h-0">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className={`group relative rounded-2xl p-[1.5px] bg-gradient-to-br ${service.accent} opacity-90 hover:opacity-100 transition-all duration-300 animate-fade-in-up ${isDarkMode ? 'shadow-[0_0_20px_rgba(0,0,0,0.5)]' : ''}`}
                    style={{ animationDelay: `${index * 90}ms` }}
                  >
                    <div className={`relative h-full flex flex-col justify-between rounded-2xl backdrop-blur-xl p-4 transition-all duration-300 group-hover:-translate-y-1.5 group-hover:scale-[1.02] shadow-md ${service.glow} group-hover:shadow-2xl ring-1 ring-transparent ${service.ring} overflow-hidden ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-[#111827] to-[#0F172A]' 
                        : 'bg-white/90'
                    }`}>

                      {/* subtle glow orb inside card on hover */}
                      <div className={`pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${service.accent} opacity-0 group-hover:opacity-30 blur-2xl transition-opacity duration-300`}></div>

                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-2.5">
                          <div className={`relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${service.accent} shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 ${isDarkMode ? 'shadow-[0_0_15px_rgba(99,102,241,0.3)]' : ''}`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className={`text-[9px] font-bold tracking-wide uppercase bg-gradient-to-r ${service.accent} bg-clip-text text-transparent mt-1`}>
                            {service.tag}
                          </span>
                        </div>

                        <h2 className={`text-sm sm:text-base font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {service.title}
                        </h2>
                        <p className={`text-[11px] sm:text-xs leading-relaxed line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {service.description}
                        </p>
                      </div>

                      <div className={`absolute bottom-0 left-4 right-4 h-[2px] rounded-full bg-gradient-to-r ${service.accent} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsServicePage;